import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BusinessLocationContextService } from './business-location-context.service';

type QueryParams = Record<string, string | number | boolean | null | undefined>;
type ApiTarget = 'identity' | 'business' | 'marketplace';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly identityBaseUrl = environment.identityApiBaseUrl.replace(/\/$/, '');
  private readonly businessBaseUrl = environment.businessApiBaseUrl.replace(/\/$/, '');
  private readonly marketplaceBaseUrl = environment.marketplaceApiBaseUrl.replace(/\/$/, '');
  private readonly locationContext = inject(BusinessLocationContextService);

  get<T>(
    path: string,
    params?: QueryParams,
    target: ApiTarget = 'identity',
    timeoutMs = environment.apiTimeoutMs,
  ): Observable<T> {
    return this.withTimeout(
      this.http.get<T>(this.url(path, target), { params: this.params(params), headers: this.headers() }),
      timeoutMs,
    );
  }

  post<T, B = unknown>(
    path: string,
    body: B,
    target: ApiTarget = 'identity',
    timeoutMs = environment.apiTimeoutMs,
  ): Observable<T> {
    return this.withTimeout(this.http.post<T>(this.url(path, target), body, { headers: this.headers() }), timeoutMs);
  }

  put<T, B = unknown>(
    path: string,
    body: B,
    target: ApiTarget = 'identity',
    timeoutMs = environment.apiTimeoutMs,
  ): Observable<T> {
    return this.withTimeout(this.http.put<T>(this.url(path, target), body, { headers: this.headers() }), timeoutMs);
  }

  patch<T, B = unknown>(path: string, body: B, target: ApiTarget = 'identity'): Observable<T> {
    return this.withTimeout(this.http.patch<T>(this.url(path, target), body, { headers: this.headers() }));
  }

  delete<T>(path: string, target: ApiTarget = 'identity'): Observable<T> {
    return this.withTimeout(this.http.delete<T>(this.url(path, target), { headers: this.headers() }));
  }

  download(path: string, params?: QueryParams, target: ApiTarget = 'identity'): Observable<Blob> {
    return this.withTimeout(
      this.http.get(this.url(path, target), { params: this.params(params), headers: this.headers(), responseType: 'blob' }),
      60_000,
    );
  }

  private url(path: string, target: ApiTarget): string {
    const baseUrl = this.baseUrl(target);
    return `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  }

  private baseUrl(target: ApiTarget): string {
    if (target === 'business') {
      return this.businessBaseUrl;
    }

    if (target === 'marketplace') {
      return this.marketplaceBaseUrl;
    }

    return this.identityBaseUrl;
  }

  private withTimeout<T>(
    request: Observable<T>,
    timeoutMs = environment.apiTimeoutMs,
  ): Observable<T> {
    return request.pipe(timeout(timeoutMs));
  }

  private params(params?: QueryParams): HttpParams {
    let httpParams = new HttpParams();

    Object.entries(params ?? {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }

  private headers(): HttpHeaders {
    const locationId = this.locationContext.selectedLocationId();
    return locationId ? new HttpHeaders({ 'X-Business-Location-Id': locationId }) : new HttpHeaders();
  }
}
